import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  formatNumber,
  PlayIcon,
  UserGroupIcon,
  XCircleIcon,
} from "@";
import RefetchDataOnError from "./RefetchData";
import { useVerificationStatisticsQuery } from "../api/queries";

interface VerificationNumbersProps {
  fromDate?: string;
  toDate?: string;
}

type StatTone =
  | "purple"
  | "indigo"
  | "amber"
  | "emerald"
  | "rose"
  | "sky"
  | "slate";

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone: StatTone;
  isLoading: boolean;
  className?: string;
}

const TONE_STYLES: Record<
  StatTone,
  {
    chip: string;
    accent: string;
    valueText: string;
    halo: string;
  }
> = {
  purple: {
    chip: "from-purple-600 to-purple-900 dark:from-purple-600 dark:to-purple-800",
    accent:
      "from-purple-500 to-purple-700 dark:from-purple-400 dark:to-purple-600",
    valueText: "text-purple-900 dark:text-purple-200",
    halo: "from-purple-200/40 dark:from-purple-700/30",
  },
  indigo: {
    chip: "from-indigo-500 to-indigo-700 dark:from-indigo-500 dark:to-indigo-700",
    accent:
      "from-indigo-500 to-indigo-700 dark:from-indigo-400 dark:to-indigo-600",
    valueText: "text-indigo-900 dark:text-indigo-200",
    halo: "from-indigo-200/40 dark:from-indigo-700/30",
  },
  amber: {
    chip: "from-amber-400 to-amber-600 dark:from-amber-500 dark:to-amber-700",
    accent: "from-amber-400 to-amber-600 dark:from-amber-400 dark:to-amber-600",
    valueText: "text-amber-900 dark:text-amber-200",
    halo: "from-amber-200/40 dark:from-amber-700/30",
  },
  emerald: {
    chip: "from-emerald-500 to-emerald-700 dark:from-emerald-500 dark:to-emerald-700",
    accent:
      "from-emerald-500 to-emerald-700 dark:from-emerald-400 dark:to-emerald-600",
    valueText: "text-emerald-900 dark:text-emerald-200",
    halo: "from-emerald-200/40 dark:from-emerald-700/30",
  },
  rose: {
    chip: "from-rose-500 to-rose-700 dark:from-rose-500 dark:to-rose-700",
    accent: "from-rose-500 to-rose-700 dark:from-rose-400 dark:to-rose-600",
    valueText: "text-rose-900 dark:text-rose-200",
    halo: "from-rose-200/40 dark:from-rose-700/30",
  },
  sky: {
    chip: "from-sky-500 to-sky-700 dark:from-sky-500 dark:to-sky-700",
    accent: "from-sky-500 to-sky-700 dark:from-sky-400 dark:to-sky-600",
    valueText: "text-sky-900 dark:text-sky-200",
    halo: "from-sky-200/40 dark:from-sky-700/30",
  },
  slate: {
    chip: "from-slate-500 to-slate-700 dark:from-slate-500 dark:to-slate-700",
    accent: "from-slate-400 to-slate-600 dark:from-slate-400 dark:to-slate-600",
    valueText: "text-slate-900 dark:text-slate-200",
    halo: "from-slate-200/40 dark:from-slate-700/30",
  },
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  tone,
  isLoading,
  className = "",
}: StatCardProps) => {
  const t = TONE_STYLES[tone];
  return (
    <div
      className={`group/stat relative h-full overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-gray-900/[0.04] dark:border-gray-700/60 dark:bg-gray-800 ${className}`}
    >
      {/* Decorative corner halo */}
      <span
        aria-hidden
        className={`pointer-events-none absolute -end-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br ${t.halo} to-transparent blur-2xl`}
      />

      <div className="relative flex items-start gap-3 md:gap-4">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${t.chip} text-white shadow-sm shadow-gray-900/10 ring-1 ring-white/10 transition-transform duration-200 group-hover/stat:scale-105 md:h-12 md:w-12`}
        >
          <Icon className="h-5 w-5 md:h-6 md:w-6" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400 md:text-[12px]">
            {title}
          </p>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            {isLoading ? (
              <span className="block h-7 w-24 animate-pulse rounded-md bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800" />
            ) : (
              <span
                className={`text-2xl font-bold tabular-nums tracking-tight ${t.valueText} md:text-[28px]`}
              >
                {formatNumber(value || 0)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const VerificationNumbers = ({
  fromDate,
  toDate,
}: VerificationNumbersProps) => {
  const {
    data: statistics,
    isError,
    refetch,
    isLoading,
  } = useVerificationStatisticsQuery(fromDate, toDate);

  if (isError) {
    return <RefetchDataOnError refetch={refetch} />;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
      {/* Featured: total — spans full width on small, full row on large */}
      <div className="sm:col-span-2 lg:col-span-3">
        <StatCard
          tone="purple"
          isLoading={isLoading}
          icon={ClipboardDocumentCheckIcon}
          title="إجمالي الطلبات"
          value={statistics?.totalConfirmedOrders || 0}
        />
      </div>

      <StatCard
        tone="slate"
        isLoading={isLoading}
        icon={PlayIcon}
        title="لم تبدأ"
        value={statistics?.notStartedCount || 0}
      />
      <StatCard
        tone="indigo"
        isLoading={isLoading}
        icon={UserGroupIcon}
        title="تم الإسناد"
        value={statistics?.assignedCount || 0}
      />
      <StatCard
        tone="emerald"
        isLoading={isLoading}
        icon={CheckCircleIcon}
        title="تم التحقق"
        value={statistics?.verifiedCount || 0}
      />
      <StatCard
        tone="rose"
        isLoading={isLoading}
        icon={XCircleIcon}
        title="مرفوض"
        value={statistics?.rejectedCount || 0}
      />
      <StatCard
        tone="amber"
        isLoading={isLoading}
        icon={ClockIcon}
        title="في انتظار التحقق"
        value={statistics?.waitingVerificationCount || 0}
      />
      <StatCard
        tone="sky"
        isLoading={isLoading}
        icon={ClockIcon}
        title="في انتظار وقت التنفيذ"
        value={statistics?.waitingSendingTimeCount || 0}
      />
    </div>
  );
};

export default VerificationNumbers;
